import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CustomerCareDTable from '../../../../../Shared/CustomerCareDTable';
import NoTabDataCard from '../../../../../Hubs/HubDetails/tabs/components/NoTabDataCard';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import { getMedicationEventHistory } from '../../../../../../store/hubOwner/actionCreators';
import {
  dimGrey,
  lightBackground1,
  outlineSeparator,
} from '../../../../../../mainTheme';
import {
  EVENT_NAME_MAPPING,
  MED_EVENT_STATUS_ID,
  SKIPPED_REASON_MAPPING,
} from '../../../../../../shared/constants';
import { getSkippedReasons } from '../../../../../../shared/utils';

interface ScheduleEventHistoryProps {
  customerId: number;
  event: { id: string };
}

interface Dose {
  id: number;
  drug_name: string;
}

const tooltipTextSx = {
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.2px',
};

const ScheduleEventHistory: React.FC<ScheduleEventHistoryProps> = ({
  customerId,
  event,
}) => {
  const dispatch = useDispatch();

  const medEventHistory = useSelector(
    (state: RootState) => state.hubOwner.medEventHistory,
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let ignore = false; // Used to prevent setting state if the component unmounts before async fetch completes
    const fetchHistory = async () => {
      setIsLoading(true);
      try {
        await dispatch(
          getMedicationEventHistory(Number(customerId), event.id, false),
        );
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    if (
      !medEventHistory ||
      medEventHistory.ownerId !== Number(customerId) ||
      medEventHistory.medEventId !== event.id
    ) {
      fetchHistory();
    }
    return () => {
      ignore = true;
    };
  }, [medEventHistory, dispatch, customerId, event.id]);

  const rows = (medEventHistory?.history || []).map((row: any, idx: number) => {
    // dispensed_flow logic (example: show "Missed" if past_due)
    let statusLabel = EVENT_NAME_MAPPING[row.status_id] || row.status_id;
    if (
      row.status_id === MED_EVENT_STATUS_ID.Dispensed &&
      row.event_data &&
      row.event_data.dispensed_flow === 'past_due'
    ) {
      statusLabel = EVENT_NAME_MAPPING[MED_EVENT_STATUS_ID.Missed];
    } else if (
      row.status_id === MED_EVENT_STATUS_ID.Dispensed &&
      row.event_data &&
      row.event_data.dispensed_flow === 'on_the_go'
    ) {
      statusLabel = EVENT_NAME_MAPPING[MED_EVENT_STATUS_ID.PreDispensed];
    }
    return {
      ...row,
      id: idx,
      statusLabel,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'statusLabel',
      headerName: 'Med status',
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) => {
        const doses: Dose[] = params.row.doses || [];
        const skippedReasons = getSkippedReasons(params.row);
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  p: 2,
                  bgcolor: lightBackground1,
                  border: `1px solid ${outlineSeparator}`,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  maxWidth: {
                    xs: '18.75rem',
                    sm: '37.5rem',
                    md: '53.125rem',
                    lg: '62.5rem',
                    xl: '78.125rem',
                  },
                  color: dimGrey,
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                {doses.map((dose) => (
                  <Typography key={dose.id} sx={tooltipTextSx}>
                    {dose.drug_name}
                  </Typography>
                ))}
                {skippedReasons.length > 0 && (
                  <Typography sx={tooltipTextSx}>Skipped reasons:</Typography>
                )}
                {skippedReasons.map((reason) => (
                  <Typography key={reason} sx={tooltipTextSx}>
                    {SKIPPED_REASON_MAPPING[reason] || reason}
                  </Typography>
                ))}
              </Box>
            }
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {params.row.statusLabel}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'occured_at',
      headerName: 'Event time',
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) =>
        new Date(params.value).toLocaleString(),
    },
    {
      field: 'sent_by',
      headerName: 'Sent by',
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.value
          ? `${params.value.customer_first_name} ${params.value.customer_last_name}`
          : 'N/A',
    },
  ];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 320,
        overflowY: 'auto',
      }}
    >
      {rows.length ? (
        <CustomerCareDTable
          rows={rows}
          columns={columns}
          pageSize={5}
          defaultSortField="occured_at"
          defaultSortDirection="desc"
          hideViewAllButton
          idField="id"
        />
      ) : (
        <NoTabDataCard />
      )}
    </Box>
  );
};

export default ScheduleEventHistory;
